import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {WalletContext} from '#/contexts/wallet-provider';
import {useInvalidateQueries} from '#/hooks/query/base/use-invalidate-queries';
import {useLandTokenContract} from '#/hooks/query/use-land-token-contract';
import useERC20Contract from '#/hooks/use-erc20-contract';
import {TransactionFlow, useTransactionFlow} from '#/hooks/use-transaction-flow';
import {useContext} from 'react';
import {TransactionType} from 'shareland-common';

export type TransactionVariables = {
  [TransactionType.Purchase]: {
    landTokenAmount: number;
    stableTokenAmount: number;
    marketPrice: number;
    transactionFee: number;
    totalStableAmount: number;
    maxSlippagePercentage: number;
    minReceivedLandAmount: number;
  };
  [TransactionType.Sell]: {
    landTokenAmount: number;
    stableTokenAmount: number;
    marketPrice: number;
    transactionFee: number;
    receiveStableAmount: number;
    maxSlippagePercentage: number;
    minReceivedStableAmount: number;
    minReceivedStableAmountWithFee: number;
  };
  [TransactionType.OpenCollateralPosition]: {
    landTokenAmount: number;
    stableTokenAmount: number;
    transactionFee: number;
    totalStableAmount: number;
    collateralRatio: number;
  };
  [TransactionType.CloseCollateralPosition]: {
    borrowedLandTokenAmount: number;
    stableTokenInCollateralAmount: number;
  };
  [TransactionType.AdjustCollateralPosition]: {
    landTokenAmount: number;
    stableTokenAmount: number;
    transactionFee: number;
    totalStableAmount: number;
    collateralRatio: number;
    stableAmountPositionDifference: number;
  };
  [TransactionType.MintLiquidityToken]: {
    landTokenAmount: number;
    stableTokenAmount: number;
    transactionFee: number;
    rentStableAmount: number;
    rentStableAmountWithFee: number;
    lpTokenAmount: number;
  };
  [TransactionType.BurnLiquidityToken]: {
    landTokenAmount: number;
    stableTokenAmount: number;
    lpTokenAmount: number;
  };
  [TransactionType.LiquidateCollateralPosition]: {};
  [TransactionType.ExecuteCollateralPositionLimitOrder]: {};
  [TransactionType.ClaimRewards]: {
    claimableStableAmount: number;
  };
  [TransactionType.PurchaseLimitOrder]: {
    landTokenAmount: number;
    stableTokenAmount: number;
    purchaseTransactionFee: number;
    transactionFee: number;
    totalStableAmount: number;
    averagePrice: number;
  };
  [TransactionType.SellLimitOrder]: {
    landTokenAmount: number;
    stableTokenAmount: number;
    sellTransactionFee: number;
    transactionFee: number;
    receiveStableAmount: number;
    targetPrice: number;
  };
};

type TransactionDataGeneric<T extends TransactionType> = {
  type: T;
  data: TransactionVariables[T];
};
export type TransactionData =
  | TransactionDataGeneric<TransactionType.Purchase>
  | TransactionDataGeneric<TransactionType.Sell>
  | TransactionDataGeneric<TransactionType.OpenCollateralPosition>
  | TransactionDataGeneric<TransactionType.CloseCollateralPosition>
  | TransactionDataGeneric<TransactionType.AdjustCollateralPosition>
  | TransactionDataGeneric<TransactionType.MintLiquidityToken>
  | TransactionDataGeneric<TransactionType.BurnLiquidityToken>
  | TransactionDataGeneric<TransactionType.ClaimRewards>
  | TransactionDataGeneric<TransactionType.PurchaseLimitOrder>
  | TransactionDataGeneric<TransactionType.SellLimitOrder>;

type UseTransactionFlowByType = [TransactionFlow, () => Promise<void>];
export const useTransactionFlowByType = {
  [TransactionType.Purchase]: (
    transactionVars: TransactionVariables[TransactionType.Purchase],
  ): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const erc20Contract = useERC20Contract();
    const landTokenContract = useLandTokenContract(land);

    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.Purchase, data: transactionVars},
      options: {
        notReady: !(erc20Contract && landTokenContract),
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      const {stableTokenAmount, minReceivedLandAmount, totalStableAmount} = transactionVars;
      return startTransactionFlow(
        () => erc20Contract!.approveLandTransaction(land, totalStableAmount),
        () => landTokenContract!.purchaseWithStableToken(stableTokenAmount, minReceivedLandAmount),
        () => {
          invalidateQueries.landInfosAndBalances(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
  [TransactionType.Sell]: (transactionVars: TransactionVariables[TransactionType.Sell]): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const landTokenContract = useLandTokenContract(land);
    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.Sell, data: transactionVars},
      options: {
        notReady: !landTokenContract,
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      const {landTokenAmount, minReceivedStableAmount} = transactionVars;
      startTransactionFlow(
        null,
        () => landTokenContract!.sell(landTokenAmount, minReceivedStableAmount),
        () => {
          invalidateQueries.landInfosAndBalances(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
  [TransactionType.MintLiquidityToken]: (
    transactionVars: TransactionVariables[TransactionType.MintLiquidityToken],
  ): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const erc20Contract = useERC20Contract();
    const landTokenContract = useLandTokenContract(land);
    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.MintLiquidityToken, data: transactionVars},
      options: {
        notReady: !(erc20Contract && landTokenContract),
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      const {landTokenAmount, rentStableAmountWithFee, rentStableAmount} = transactionVars;
      startTransactionFlow(
        () => erc20Contract!.approveLandTransaction(land, rentStableAmountWithFee),
        () => landTokenContract!.provideLiquidity(landTokenAmount, rentStableAmount),
        () => {
          invalidateQueries.landInfosAndBalances(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
  [TransactionType.BurnLiquidityToken]: (
    transactionVars: TransactionVariables[TransactionType.BurnLiquidityToken],
  ): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const landTokenContract = useLandTokenContract(land);
    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.BurnLiquidityToken, data: transactionVars},
      options: {
        notReady: !landTokenContract,
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      const {lpTokenAmount} = transactionVars;
      startTransactionFlow(
        null,
        () => landTokenContract!.burnLiquidityProviderToken(lpTokenAmount),
        () => {
          invalidateQueries.landInfosAndBalances(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
  [TransactionType.OpenCollateralPosition]: (
    transactionVars: TransactionVariables[TransactionType.OpenCollateralPosition],
  ): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const erc20Contract = useERC20Contract();
    const landTokenContract = useLandTokenContract(land);
    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.OpenCollateralPosition, data: transactionVars},
      options: {
        notReady: !(erc20Contract && landTokenContract),
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      const {landTokenAmount, stableTokenAmount, totalStableAmount} = transactionVars;

      startTransactionFlow(
        () => erc20Contract!.approve(landTokenContract!.land.contractAddresses.collateralPosition, totalStableAmount),
        () => landTokenContract!.openCollateralPosition(landTokenAmount, stableTokenAmount),
        () => {
          invalidateQueries.landInfosAndBalances(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
  [TransactionType.AdjustCollateralPosition]: (
    transactionVars: TransactionVariables[TransactionType.AdjustCollateralPosition],
  ): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const erc20Contract = useERC20Contract();
    const landTokenContract = useLandTokenContract(land);
    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.OpenCollateralPosition, data: transactionVars},
      options: {
        notReady: !(erc20Contract && landTokenContract),
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      const {landTokenAmount, stableTokenAmount, stableAmountPositionDifference, transactionFee} = transactionVars;

      // We only need to approve the transaction if we are adding more stable tokens to the position
      const approvalCallback =
        stableAmountPositionDifference > 0
          ? () =>
              erc20Contract!.approve(
                landTokenContract!.land.contractAddresses.collateralPosition,
                stableAmountPositionDifference + transactionFee,
              )
          : null;

      startTransactionFlow(
        approvalCallback,
        () => landTokenContract!.adjustCollateralPosition(landTokenAmount, stableTokenAmount),
        () => {
          invalidateQueries.landInfosAndBalances(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
  [TransactionType.CloseCollateralPosition]: (
    transactionVars: TransactionVariables[TransactionType.CloseCollateralPosition],
  ): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const landTokenContract = useLandTokenContract(land);
    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.CloseCollateralPosition, data: transactionVars},
      options: {
        notReady: !landTokenContract,
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      const {borrowedLandTokenAmount, stableTokenInCollateralAmount} = transactionVars;

      startTransactionFlow(
        null,
        () => landTokenContract!.closeCollateralPosition(borrowedLandTokenAmount, stableTokenInCollateralAmount),
        () => {
          invalidateQueries.landInfosAndBalances(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
  [TransactionType.ClaimRewards]: (
    transactionVars: TransactionVariables[TransactionType.ClaimRewards],
  ): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const landTokenContract = useLandTokenContract(land);
    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.ClaimRewards, data: transactionVars},
      options: {
        notReady: !landTokenContract,
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      startTransactionFlow(
        null,
        () => landTokenContract!.claimRewards(),
        () => {
          invalidateQueries.landInfosAndBalances(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
  [TransactionType.PurchaseLimitOrder]: (
    transactionVars: TransactionVariables[TransactionType.PurchaseLimitOrder],
  ): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const erc20Contract = useERC20Contract();
    const landTokenContract = useLandTokenContract(land);
    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.PurchaseLimitOrder, data: transactionVars},
      options: {
        notReady: !(erc20Contract && landTokenContract),
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      const {landTokenAmount, stableTokenAmount, purchaseTransactionFee, totalStableAmount} = transactionVars;

      return startTransactionFlow(
        () => erc20Contract!.approve(landTokenContract!.land.contractAddresses.orderBook, totalStableAmount),
        () => landTokenContract!.createPurchaseOrder(landTokenAmount, stableTokenAmount + purchaseTransactionFee),
        () => {
          invalidateQueries.limitOrders(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
  [TransactionType.SellLimitOrder]: (
    transactionVars: TransactionVariables[TransactionType.SellLimitOrder],
  ): UseTransactionFlowByType => {
    const {land, landMarketInfo} = useContext(ActionMenuContext);

    const erc20Contract = useERC20Contract();
    const landTokenContract = useLandTokenContract(land);
    const [transactionFlow, startTransactionFlow] = useTransactionFlow({
      land,
      landMarketInfo,
      transactionData: {type: TransactionType.SellLimitOrder, data: transactionVars},
      options: {
        notReady: !(erc20Contract && landTokenContract),
      },
    });
    const invalidateQueries = useTransactionFlowInvalidateQueries();

    const start = async () => {
      const {landTokenAmount, stableTokenAmount, transactionFee, sellTransactionFee} = transactionVars;

      return startTransactionFlow(
        () => erc20Contract!.approve(landTokenContract!.land.contractAddresses.orderBook, transactionFee),
        () => landTokenContract!.createSellOrder(landTokenAmount, stableTokenAmount + sellTransactionFee),
        () => {
          invalidateQueries.limitOrders(land.id);
        },
      );
    };

    return [transactionFlow, start];
  },
};

const useTransactionFlowInvalidateQueries = () => {
  const invalidateQueries = useInvalidateQueries();
  const {
    accountResult: {address},
  } = useContext(WalletContext);

  const landInfosAndBalances = (landId: number) => {
    invalidateQueries.useLandMarketInfos(landId);
    invalidateQueries.useLandAccountInfos(address, landId);
    invalidateQueries.useStableTokenBalance();
    invalidateQueries.useNativeTokenBalance();
  };

  const limitOrders = (landId: number) => {
    landInfosAndBalances(landId);
    invalidateQueries.useLimitOrdersIds(landId);
    invalidateQueries.useLimitOrders(landId);
  };

  return {
    landInfosAndBalances,
    limitOrders,
  };
};
